import { CheckCircle2, AlertCircle, Scale } from "lucide-react";

/**
 * <ValidityBadge/> + <DecisionList/> — surfaces the Validity Engine and
 * Decision Engine output as a single, friendly card on the client portal.
 *
 *   validity = { level, label, ready_pct, checks: [], missing: [] }
 *   decisions = { decisions: [{id, label, status}], confirmed_count, total_count }
 */
export default function ValidityPanel({ validity, decisions }) {
  if (!validity) return null;
  const tone = {
    legally_ready:         "bg-emerald-100 text-emerald-800",
    structurally_complete: "bg-blue-100   text-blue-800",
    not_ready:             "bg-amber-100  text-amber-900",
  }[validity.level] || "bg-zinc-100 text-zinc-700";

  return (
    <div className="bg-white border border-zinc-200" data-testid="validity-panel">
      <div className="px-6 py-5 border-b border-zinc-200 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 bg-zinc-900 text-white grid place-items-center mt-0.5">
            <Scale className="h-4 w-4" />
          </div>
          <div>
            <div className="text-overline text-zinc-500">Legal status</div>
            <h3 className="font-serif text-2xl tracking-tight mt-1" data-testid="validity-headline">
              {validity.label}
            </h3>
            <p className="mt-1 text-sm text-zinc-600 max-w-xl">
              {validity.level === "legally_ready"
                ? "Every legal input required for this matter is in place. Your lawyer can proceed to drafting and execution."
                : validity.level === "structurally_complete"
                  ? "All blocking legal inputs are in place. A few best-practice items remain — completing them lifts the matter to legally ready."
                  : "Some legal inputs are still missing. Complete the items below to make this matter legally ready."}
            </p>
          </div>
        </div>
        <span className={`text-[10px] tracking-widest uppercase px-2 py-1 ${tone}`} data-testid="validity-pill">
          {validity.label}
        </span>
      </div>

      {/* Validity checks */}
      <ul className="divide-y divide-zinc-100" data-testid="validity-checks">
        {validity.checks.map((c) => (
          <li key={c.id} className="px-6 py-3 flex items-start gap-3"
              data-testid={`validity-check-${c.id}`}>
            {c.satisfied
              ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              : <AlertCircle  className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-zinc-900">{c.label}</div>
              {c.detail && (
                <div className="text-xs text-zinc-500 mt-0.5">{c.detail}</div>
              )}
            </div>
            <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 shrink-0 ${
              c.satisfied
                ? "bg-emerald-100 text-emerald-800"
                : c.blocking
                  ? "bg-amber-100 text-amber-900"
                  : "bg-zinc-100 text-zinc-700"
            }`}>
              {c.satisfied ? "Confirmed" : c.blocking ? "Required" : "Best practice"}
            </span>
          </li>
        ))}
      </ul>

      {/* Decisions */}
      {decisions?.decisions?.length > 0 && (
        <div className="border-t-2 border-zinc-200" data-testid="decision-list">
          <div className="px-6 py-3 bg-zinc-50 text-overline text-zinc-700 flex items-center justify-between">
            <span>Key legal decisions</span>
            <span className="text-[10px] tracking-widest uppercase text-zinc-500">
              {decisions.confirmed_count} / {decisions.total_count}
            </span>
          </div>
          <ul className="divide-y divide-zinc-100">
            {decisions.decisions.map((d) => (
              <li key={d.id}
                  className="px-6 py-2.5 flex items-center gap-3"
                  data-testid={`decision-${d.id}`}>
                {d.status === "confirmed"
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  : <AlertCircle  className="h-4 w-4 text-amber-600 shrink-0" />}
                <span className="text-sm flex-1 min-w-0">{d.label}</span>
                <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 shrink-0 ${
                  d.status === "confirmed"
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-amber-100 text-amber-900"
                }`}>
                  {d.status === "confirmed" ? "Decision confirmed" : "Decision incomplete"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
